import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { usePlugin } from '../hooks/usePlugins';
import PurchasePopup from '../components/PurchasePopup';
import {
  ArrowLeft,
  Download,
  Star,
  Shield,
  Clock,
  HardDrive,
  CheckCircle2,
  ExternalLink,
  Sparkles,
  ShoppingCart,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PluginDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPurchasePopup, setShowPurchasePopup] = useState(false);
  
  const { plugin, loading, error } = usePlugin(id);

  const handlePurchase = () => {
    if (plugin?.price === 0) {
      // Free plugin - direct download
      if (plugin.downloadLink) {
        window.open(plugin.downloadLink, '_blank');
        toast({
          title: 'Download Started!',
          description: 'Your plugin is downloading now. Check Discord for installation help.',
        });
      } else {
        toast({
          title: 'Download Link Not Available',
          description: 'Please contact support for download instructions.',
          variant: 'destructive'
        });
      }
    } else {
      // Premium plugin - show purchase popup
      setShowPurchasePopup(true);
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Premium':
        return 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black';
      case 'New':
        return 'bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] text-white';
      case 'Free':
        return 'bg-gradient-to-r from-[#0EA5FF] to-[#3B82F6] text-white';
      default:
        return 'bg-gray-700 text-white';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#0B0F13' }}>
        <div className="text-center">
          <div className="animate-spin w-16 h-16 border-4 border-[#00FFD1] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading plugin details...</p>
        </div>
      </div>
    );
  }

  if (error || !plugin) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#0B0F13' }}>
        <div className="text-center">
          <h1 className="text-4xl font-semibold mb-4" style={{ color: '#FFFFFF' }}>
            Plugin Not Found
          </h1>
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#0B0F13', minHeight: '100vh' }}>
      {/* Header */}
      <header
        className="fixed top-0 w-full z-50"
        style={{
          background: '#000000',
          borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
          padding: '16px 7.6923%',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <Sparkles className="w-8 h-8" style={{ color: '#00FFD1' }} />
          <span className="text-2xl font-semibold" style={{ color: '#FFFFFF', fontWeight: 600 }}>
            Elite Plugin Hub
          </span>
        </div>

        <Button
          onClick={() => navigate('/')}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
            borderRadius: '0px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 500,
            border: 'none',
          }}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Plugins
        </Button>
      </header>

      {/* Main Content */}
      <div style={{ paddingTop: '80px' }}>
        <div className="container mx-auto px-8 md:px-16 py-20">
          {/* Plugin Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
            {/* Left: 3D Preview */}
            <div className="relative">
              <div
                className="w-full aspect-square flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6, #0EA5FF)',
                  borderRadius: '20px',
                  boxShadow: '0 0 80px rgba(14, 165, 255, 0.6)',
                  animation: 'float 6s ease-in-out infinite',
                }}
              >
                <div className="text-center">
                  <Sparkles className="w-32 h-32 mx-auto mb-4" style={{ color: '#FFFFFF' }} />
                  <p className="text-2xl font-semibold" style={{ color: '#FFFFFF' }}>
                    {plugin.name}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Plugin Info */}
            <div className="space-y-6">
              <div>
                <Badge className={`${getBadgeColor(plugin.badge)} px-4 py-2 text-base mb-4`}>
                  {plugin.badge}
                </Badge>
                <h1
                  className="text-5xl font-semibold mb-4"
                  style={{ color: '#FFFFFF', fontWeight: 600, letterSpacing: '-0.02em' }}
                >
                  {plugin.name}
                </h1>
                <p className="text-xl mb-4" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {plugin.tagline}
                </p>
                <div className="flex items-center gap-4 text-lg" style={{ color: '#4D4D4D' }}>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 fill-current" style={{ color: '#D4AF37' }} />
                    <span>{plugin.rating} ({plugin.reviews} reviews)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    <span>{plugin.downloads?.toLocaleString()} downloads</span>
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <Card
                style={{
                  background: '#121212',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '0px',
                  padding: '24px',
                }}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6" style={{ color: '#0EA5FF' }} />
                    <div>
                      <div className="text-sm" style={{ color: '#4D4D4D' }}>
                        Version
                      </div>
                      <div className="font-semibold" style={{ color: '#FFFFFF' }}>
                        {plugin.version}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <HardDrive className="w-6 h-6" style={{ color: '#8B5CF6' }} />
                    <div>
                      <div className="text-sm" style={{ color: '#4D4D4D' }}>
                        File Size
                      </div>
                      <div className="font-semibold" style={{ color: '#FFFFFF' }}>
                        {plugin.fileSize}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6" style={{ color: '#00FFD1' }} />
                    <div>
                      <div className="text-sm" style={{ color: '#4D4D4D' }}>
                        Last Updated
                      </div>
                      <div className="font-semibold" style={{ color: '#FFFFFF' }}>
                        {plugin.lastUpdated ? new Date(plugin.lastUpdated).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Download className="w-6 h-6" style={{ color: '#D4AF37' }} />
                    <div>
                      <div className="text-sm" style={{ color: '#4D4D4D' }}>
                        Downloads
                      </div>
                      <div className="font-semibold" style={{ color: '#FFFFFF' }}>
                        {plugin.downloads?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Price and Purchase */}
              <Card
                style={{
                  background: '#121212',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '0px',
                  padding: '32px',
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-sm mb-1" style={{ color: '#4D4D4D' }}>
                      {plugin.price === 0 ? 'Free Download' : 'One-Time Payment'}
                    </div>
                    <div
                      className="text-5xl font-semibold"
                      style={{ color: plugin.price === 0 ? '#0EA5FF' : '#D4AF37', fontWeight: 600 }}
                    >
                      {plugin.price === 0 ? 'FREE' : `$${plugin.price}`}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={handlePurchase}
                  className="w-full"
                  style={{
                    background: '#00FFD1',
                    color: '#000000',
                    borderRadius: '0px',
                    padding: '16px 24px',
                    fontSize: '18px',
                    fontWeight: 500,
                    minHeight: '56px',
                    border: 'none',
                    transition: 'all 0.4s ease-in-out',
                  }}
                >
                  {plugin.price === 0 ? (
                    <>
                      <Download className="w-5 h-5 mr-2 inline" />
                      Free Download
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2 inline" />
                      Purchase Plugin
                    </>
                  )}
                </Button>
                <p className="text-center text-sm mt-4" style={{ color: '#4D4D4D' }}>
                  Works with Paper, Spigot & Geyser • Auto-updates • 30-day support
                </p>
              </Card>
            </div>
          </div>

          {/* Description */}
          <section className="mb-16">
            <h2
              className="text-3xl font-semibold mb-6"
              style={{ color: '#FFFFFF', fontWeight: 600 }}
            >
              Description
            </h2>
            <p className="text-lg" style={{ color: 'rgba(255, 255, 255, 0.85)', lineHeight: 1.6 }}>
              {plugin.description}
            </p>
          </section>

          {/* Features */}
          <section className="mb-16">
            <h2
              className="text-3xl font-semibold mb-6"
              style={{ color: '#FFFFFF', fontWeight: 600 }}
            >
              Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(plugin.features || []).map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4"
                  style={{
                    background: '#121212',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    borderRadius: '0px',
                  }}
                >
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#00FFD1' }} />
                  <span style={{ color: 'rgba(255, 255, 255, 0.85)' }}>{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Compatibility */}
          <section className="mb-16">
            <h2
              className="text-3xl font-semibold mb-6"
              style={{ color: '#FFFFFF', fontWeight: 600 }}
            >
              Compatibility
            </h2>
            <div className="flex flex-wrap gap-4">
              {(plugin.compatibility || []).map((comp, index) => (
                <Badge
                  key={index}
                  className="px-6 py-3 text-base"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                  }}
                >
                  {comp}
                </Badge>
              ))}
            </div>
          </section>

          {/* Changelog */}
          <section className="mb-16">
            <h2
              className="text-3xl font-semibold mb-6"
              style={{ color: '#FFFFFF', fontWeight: 600 }}
            >
              Changelog
            </h2>
            <Card
              style={{
                background: '#121212',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '0px',
                padding: '24px',
              }}
            >
              <pre
                className="whitespace-pre-wrap font-mono text-base"
                style={{ color: 'rgba(255, 255, 255, 0.85)' }}
              >
                {plugin.changelog || 'No changelog available'}
              </pre>
            </Card>
          </section>

          {/* Demo Server & Support */}
          <section>
            <h2
              className="text-3xl font-semibold mb-6"
              style={{ color: '#FFFFFF', fontWeight: 600 }}
            >
              Demo & Support
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                style={{
                  background: '#121212',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '0px',
                  padding: '24px',
                }}
              >
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                  Demo Server
                </h3>
                <p className="mb-4" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Try this plugin on our live demo server:
                </p>
                <div
                  className="flex items-center gap-2 p-3"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0px',
                  }}
                >
                  <code style={{ color: '#00FFD1' }}>{plugin.demoServer || 'No demo available'}</code>
                  <ExternalLink className="w-4 h-4" style={{ color: '#0EA5FF' }} />
                </div>
              </Card>

              <Card
                style={{
                  background: '#121212',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  borderRadius: '0px',
                  padding: '24px',
                }}
              >
                <h3 className="text-xl font-semibold mb-4" style={{ color: '#FFFFFF' }}>
                  Support Channel
                </h3>
                <p className="mb-4" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Get help from our support team:
                </p>
                <div
                  className="flex items-center gap-2 p-3"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0px',
                  }}
                >
                  <code style={{ color: '#8B5CF6' }}>{plugin.supportChannel || 'No support channel'}</code>
                  <ExternalLink className="w-4 h-4" style={{ color: '#0EA5FF' }} />
                </div>
              </Card>
            </div>
          </section>
        </div>
      </div>

      {/* Purchase Popup */}
      {plugin && (
        <PurchasePopup 
          plugin={plugin}
          isOpen={showPurchasePopup}
          onClose={() => setShowPurchasePopup(false)}
        />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

export default PluginDetail;